import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import {
    defaultVmDataDisk,
    environmentDetails,
    isConfigDiskFulfilled,
    isMandatoryTagsFullfilled,
    orderId,
    quickConfigDetails,
    sspSubmitOrder,
    toggleSspModels,
    toggleSapPgtRequest,
    isSapSidValid,
    isValidProvisioningForm,
    isQuickConfigSelected,
    isHealthProbeFullfilled,
} from 'pages/provisioning/store/vm';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { linuxSLESCatalogDefinitionId, SlesPgtCatalogDefinitionId, SlesPgtDBCatalogDefinitionId } from 'utils/catalogDefinitionId';
import { oelServiceType, suseServiceType } from 'utils/serviceType';
import {
    formOrderObjFromCart,
    formOrderObjFromOrderDetails,
    resetEnvironmentDetails,
    resetOrder,
    resetToggleSspModal,
    getSKUNameForSearch,
    isHpFormFilled,
} from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/vm/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/vm/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/vm/SspForm'));
const Tags = React.lazy(() => import('pages/provisioning/vm/Tags'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/vm/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/vm/UpdatedToCartConfirmModal'));
const ConfirmDD = React.lazy(() => import('pages/provisioning/vm/ConfirmDD'));
const SamRequest = React.lazy(() => import('pages/provisioning/vm/sam-request'));
const QuickConfig = React.lazy(() => import('pages/provisioning/vm/QuickConfig'));
const CurrentServerDetails = React.lazy(() => import('pages/provisioning/vm/components/CurrentServerDetails'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/vm/ShowCostDetails'));
const SapPgtConfirmModal = React.lazy(() => import('pages/provisioning/vm/SapPgtConfirmModal'));
const HealthProbeModal = React.lazy(() => import('pages/provisioning/vm/components/HealthProbeModal'));

const VmProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [vmDataDisk, setVmDataDisk] = useRecoilState(defaultVmDataDisk);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const [, resetToggleSapPgtRequest] = useRecoilState(toggleSapPgtRequest);

    const [, resetSapSidValid] = useRecoilState(isSapSidValid);
    const [, setValidProvisioningForm] = useRecoilState(isValidProvisioningForm);
    const [, setQuickConfigSelected] = useRecoilState(isQuickConfigSelected);
    const [, setIsHPConfig] = useRecoilState(isHealthProbeFullfilled);

    const { osType, serverType, catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();
    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }
    async function getCustomImages({ region }) {
        const { data } = await axios.get(`${endPoints.ssp.vm.customImages}?targetregion=${region}&name=${getSKUNameForSearch({ catalogCode })}`);
        return data;
    }

    async function getSelectedOS({ region, customImageId }) {
        const customImagesApi = await getCustomImages({ region });
        return customImagesApi?.filter((item) => customImageId?.includes(item.customImageId));
    }

    async function checkCatalog() {
        const isWindows = osType === 'Windows';
        let selector = isWindows ? 'azure_windows_vm_host' : 'azure_rhel_vm_host';
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();

        if (linuxSLESCatalogDefinitionId.includes(catalogCode) || [...SlesPgtCatalogDefinitionId, ...SlesPgtDBCatalogDefinitionId]?.includes(catalogCode)) {
            selector = 'azure_suse_vm_host';
        }

        setVmDataDisk({
            ...vmDataDisk,
            diskSizeInGib: data?.provisioning?.requestPayloadDefinition?.extra_vars[selector]?.os_disk_size_gb,
        });

        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem, osType, serverType, data?.images?.sku);
            let selectedOs = [];
            if (
                [suseServiceType, oelServiceType]?.includes(serverType) &&
                ![...SlesPgtCatalogDefinitionId, ...SlesPgtDBCatalogDefinitionId]?.includes(catalogCode)
            ) {
                const catalogConfig = cartItem?.itemDetail?.catalogService?.catalogConfig || null;
                selectedOs = await getSelectedOS({ region: catalogConfig?.environmentDetails?.region, customImageId: catalogConfig?.os?.customImageId });
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                emptyCost: costs,
                ...([suseServiceType, oelServiceType]?.includes(serverType) &&
                    ![...SlesPgtCatalogDefinitionId, ...SlesPgtDBCatalogDefinitionId]?.includes(catalogCode) && {
                        osVersion: selectedOs?.[0]?.name || null,
                        osVersionDetail: {
                            docType: selectedOs?.[0]?.docType,
                            hyperVGeneration: selectedOs?.[0]?.properties?.hyperVGeneration,
                            isActive: selectedOs?.[0]?.isActive,
                            location: cartItem?.itemDetail?.catalogService?.catalogConfig?.environmentDetails?.region,
                            marketPlaceImageId: selectedOs?.[0]?.customImageId,
                            offer: selectedOs?.[0]?.properties?.identifier?.offer,
                            publisher: selectedOs?.[0]?.properties?.identifier?.publisher,
                            sku: selectedOs?.[0]?.properties?.identifier?.sku,
                            type: selectedOs?.[0]?.type,
                            name: selectedOs?.[0]?.name,
                        },
                    }),
            }));
            if ([...SlesPgtCatalogDefinitionId, ...SlesPgtDBCatalogDefinitionId]?.includes(catalogCode)) {
                setValidProvisioningForm(false);
                setIsHPConfig(isHpFormFilled({ order }));
            }
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails, osType, serverType, data?.images?.sku);
            let selectedOs = [];
            if ([suseServiceType, oelServiceType]?.includes(serverType)) {
                const catalog = orderDetails?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || null;
                const catalogConfig = catalog?.catalogConfig || null;
                selectedOs = await getSelectedOS({ region: catalogConfig?.environmentDetails?.region, customImageId: catalogConfig?.os?.customImageId });
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                ...([suseServiceType, oelServiceType]?.includes(serverType) && {
                    osVersion: selectedOs?.[0]?.name || null,
                    osVersionDetail: {
                        docType: selectedOs?.[0]?.docType,
                        hyperVGeneration: selectedOs?.[0]?.properties?.hyperVGeneration,
                        isActive: selectedOs?.[0]?.isActive,
                        marketPlaceImageId: selectedOs?.[0]?.customImageId,
                        offer: selectedOs?.[0]?.properties?.identifier?.offer,
                        publisher: selectedOs?.[0]?.properties?.identifier?.publisher,
                        sku: selectedOs?.[0]?.properties?.identifier?.sku,
                        type: selectedOs?.[0]?.type,
                        name: selectedOs?.[0]?.name,
                    },
                }),
            }));
            if ([...SlesPgtCatalogDefinitionId, ...SlesPgtDBCatalogDefinitionId]?.includes(catalogCode)) {
                setValidProvisioningForm(false);
                setIsHPConfig(isHpFormFilled({ order }));
            }
        } else {
            setOrder((previous) => ({
                ...previous,
                osType,
                sku: data?.images?.sku,
                // catalogOffer: data?.offer,
                serverType,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }
    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetEnvDetails(resetEnvironmentDetails);
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetIsConfigDiskFulfilled(false);
        resetQuickConfig(undefined);
        resetToggleSapPgtRequest(false);
        resetSapSidValid(true);
        setQuickConfigSelected(false);
        if ([...SlesPgtCatalogDefinitionId, ...SlesPgtDBCatalogDefinitionId]?.includes(catalogCode)) {
            setValidProvisioningForm(true);
            setIsHPConfig(false);
        } else {
            setValidProvisioningForm(false);
        }
    };
    React.useEffect(() => {
        if (cmpCatalogs?.length && osType && serverType && catalogCode) {
            setLoading(true);
            reset();
            setVmDataDisk(undefined);
            checkCatalog();
        }
    }, [cmpCatalogs, osType, serverType, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>VM Provisioning - DevX Infra</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <CurrentServerDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ConfirmDD />
                        <SamRequest />
                        <QuickConfig />
                        <ShowCostDetails />
                        <HealthProbeModal />
                        <SapPgtConfirmModal />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(VmProvisioningPage);
